/**
 * パスワード＋パスキーの2段階ログイン（管理画面・マイページ共通）
 * （パスキーのみでのログインは discoverable.js）
 *
 * 1. {prefix}/login/options に ID・パスワード・reCAPTCHAトークンを送り、チケットと
 *    パスキーの認証オプション（パスキー登録済みの場合のみ）を受け取る
 * 2. 認証オプションがあればパスキーで署名する（キャンセル・失敗時はアサーションなしで続行し、
 *    サーバー側で PASSKEY_REQUIRED になる）
 * 3. {prefix}/login にチケットとアサーションを送り、ログイン後のURLへ遷移する
 *
 * パスキーのない端末では、2. の段階で「確認コードをメールで送る」を押すと
 * {prefix}/login/email-code で登録メールアドレスに確認コードが届き、
 * {prefix}/login にチケットと email_code を送ってログインできる。
 * 確認コードの欄はログインフォームを囲む .p-login 内の [data-passkey-email-code] に置く。
 */
import { startAuthentication, WebAuthnAbortService } from '@simplewebauthn/browser';
import { csrfToken } from '@/js/_common';

export const COMMON_ERROR_MESSAGES = {
    CAPTCHA_INVALID: 'reCAPTCHAの認証に失敗しました。再度お試しください。',
    RATE_LIMITED: '試行回数が多すぎます。しばらく待ってからお試しください',
    PASSKEY_REQUIRED: 'パスキーの認証が完了しませんでした。もう一度お試しください',
    PASSKEY_INVALID: 'パスキーを確認できませんでした。登録済みの端末でお試しください',
    TICKET_INVALID: '認証の有効期限が切れました。もう一度ログインしてください',
    CHALLENGE_INVALID: '認証の有効期限が切れました。もう一度お試しください',
    EMAIL_CODE_INVALID: '確認コードが正しくありません',
    EMAIL_CODE_EXPIRED: '確認コードの有効期限が切れました。もう一度送信してください',
    EMAIL_CODE_TOO_MANY: '確認コードの入力回数が上限に達しました。もう一度ログインしてください',
    EMAIL_CODE_RESEND_WAIT: 'しばらく待ってから再送してください',
    EMAIL_CODE_SEND_LIMIT: '確認コードの送信回数が上限に達しました。もう一度ログインしてください',
    EMAIL_CODE_ACCOUNT_LOCKED: '確認コードの送信・入力が多すぎるため、しばらくメールでのログインを利用できません。時間をおいてお試しください',
};
export const DEFAULT_ERROR_MESSAGE = 'ログインできませんでした。もう一度お試しください';

// パスキーの失敗後に確認コードの案内を出すエラー
const OFFER_EMAIL_CODE_ERRORS = ['PASSKEY_REQUIRED', 'PASSKEY_INVALID'];
// チケットが使えなくなり、ID・パスワードからやり直すエラー
const RESTART_ERRORS = ['TICKET_INVALID', 'EMAIL_CODE_TOO_MANY', 'EMAIL_CODE_SEND_LIMIT'];
// 再送できるまでの秒数（サーバー側の設定と合わせる）
const RESEND_WAIT_SECONDS = 60;

export const postJson = (url, body) => fetch(url, {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
        'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
});

export const readError = async (response) => {
    const data = await response.json().catch(() => null);
    if (data && data.error) return data.error;
    if (response.status === 429) return 'RATE_LIMITED';
    return null;
};

/**
 * フォームごとのログイン状態（チケット・確認コード欄）
 */
const sessions = new WeakMap();

const createSession = (form, params) => {
    const panel = form.closest('.p-login')?.querySelector('[data-passkey-email-code]') ?? null;
    const q = (selector) => (panel ? panel.querySelector(selector) : null);
    const offerArea = q('[data-email-code-offer]');
    const sendButton = q('[data-email-code-send]');
    const entryArea = q('[data-email-code-entry]');
    const sentToArea = q('[data-email-code-sent-to]');
    const codeInput = q('[data-email-code-input]');
    const submitCodeButton = q('[data-email-code-submit]');
    const resendButton = q('[data-email-code-resend]');
    const panelErrorArea = q('[data-email-code-error]');
    const resendLabel = resendButton ? resendButton.textContent.trim() : '確認コードを再送する';

    const state = {
        params,
        ticket: null,
        // ログインに送ってチケットが消費済みか（消費済みなら確認コードの送信前に options を取り直す）
        ticketConsumed: true,
        // パスキーの署名待ちの間に確認コードへ切り替えたか
        switchedToEmailCode: false,
        username: '',
        remember: false,
        busy: false,
        resendTimer: null,
    };

    const messages = () => ({ ...COMMON_ERROR_MESSAGES, INVALID_CREDENTIALS: state.params.invalidCredentialsMessage });

    const setPanelError = (code) => {
        if (!panelErrorArea) return;
        if (code === undefined) {
            panelErrorArea.textContent = '';
            panelErrorArea.hidden = true;
            return;
        }
        panelErrorArea.textContent = messages()[code] ?? DEFAULT_ERROR_MESSAGE;
        panelErrorArea.hidden = false;
    };

    const stopResendTimer = () => {
        if (state.resendTimer) {
            clearInterval(state.resendTimer);
            state.resendTimer = null;
        }
        if (resendButton) {
            resendButton.disabled = false;
            resendButton.textContent = resendLabel;
        }
    };

    const startResendTimer = () => {
        stopResendTimer();
        if (!resendButton) return;
        let remaining = RESEND_WAIT_SECONDS;
        resendButton.disabled = true;
        resendButton.textContent = `${resendLabel}（${remaining}秒）`;
        state.resendTimer = setInterval(() => {
            remaining -= 1;
            if (remaining <= 0) {
                stopResendTimer();
                return;
            }
            resendButton.textContent = `${resendLabel}（${remaining}秒）`;
        }, 1000);
    };

    const setBusy = (busy) => {
        state.busy = busy;
        [sendButton, submitCodeButton].forEach((button) => {
            if (button) button.disabled = busy;
        });
        if (resendButton && !state.resendTimer) resendButton.disabled = busy;
    };

    /** 確認コードの欄を閉じる（ログインをやり直すとき） */
    const hide = () => {
        stopResendTimer();
        setPanelError();
        if (!panel) return;
        panel.hidden = true;
        if (offerArea) offerArea.hidden = false;
        if (entryArea) entryArea.hidden = true;
        if (codeInput) codeInput.value = '';
        if (sentToArea) sentToArea.textContent = '';
    };

    /** 「確認コードをメールで送る」の案内を表示する */
    const showOffer = () => {
        if (!panel) return;
        panel.hidden = false;
        if (offerArea) offerArea.hidden = false;
        if (entryArea) entryArea.hidden = true;
    };

    const showEntry = (sentTo) => {
        if (!panel) return;
        panel.hidden = false;
        if (offerArea) offerArea.hidden = true;
        if (entryArea) entryArea.hidden = false;
        if (sentToArea) sentToArea.textContent = `${sentTo} に確認コードを送信しました`;
        if (codeInput) codeInput.focus();
    };

    const getRecaptchaToken = async () => {
        const { siteKey, actionName } = state.params;
        if (!siteKey || !window.grecaptcha?.enterprise) return null;
        try {
            return await window.grecaptcha.enterprise.execute(siteKey, { action: actionName });
        } catch (e) {
            console.error(e);
            return null;
        }
    };

    /** ① 画面の入力値で ID・パスワードを検証し、チケットを受け取る */
    const requestOptions = async () => {
        const { usernameKey, optionsUrl } = state.params;
        const recaptchaToken = await getRecaptchaToken();
        const rememberInput = form.querySelector('[name="remember"]');
        state.remember = !!rememberInput && (rememberInput.type === 'checkbox' ? rememberInput.checked : !!rememberInput.value);
        state.username = form.querySelector(`[name="${usernameKey}"]`)?.value ?? '';
        const response = await postJson(optionsUrl, {
            [usernameKey]: state.username,
            password: form.querySelector('[name="password"]')?.value ?? '',
            recaptcha_token: recaptchaToken,
        });
        if (!response.ok) {
            return { error: await readError(response) };
        }
        const { ticket, options } = await response.json();
        state.ticket = ticket;
        state.ticketConsumed = false;
        return { options };
    };

    /** ③ チケット（＋アサーションまたは確認コード）でログインする */
    const login = async (extra) => {
        const { usernameKey, loginUrl } = state.params;
        state.ticketConsumed = !('email_code' in extra);
        // ID は試行回数制限のキーを①と揃えるためだけに送る（認証には使わない）
        const response = await postJson(loginUrl, {
            [usernameKey]: state.username,
            ticket: state.ticket,
            remember: state.remember,
            ...extra,
        });
        if (!response.ok) {
            return { error: await readError(response) };
        }
        const { redirect } = await response.json();
        window.location.href = redirect;
        return { ok: true };
    };

    /** 確認コードを送る（チケットが消費済みなら画面の入力値で options を取り直す） */
    const sendCode = async () => {
        if (state.busy) return;
        setBusy(true);
        setPanelError();
        state.params.clearError?.();
        try {
            if (state.ticketConsumed || !state.ticket) {
                const result = await requestOptions();
                if (result.error !== undefined) {
                    setPanelError(result.error);
                    return;
                }
                if (!result.options) {
                    // パスキーが削除されていた場合はパスワードのみでログインできる
                    const loginResult = await login({ assertion: null });
                    if (loginResult.error !== undefined) setPanelError(loginResult.error);
                    return;
                }
            }

            const { usernameKey, emailCodeUrl } = state.params;
            const response = await postJson(emailCodeUrl, {
                [usernameKey]: state.username,
                ticket: state.ticket,
            });
            if (!response.ok) {
                const code = await readError(response);
                if (RESTART_ERRORS.includes(code)) state.ticketConsumed = true;
                setPanelError(code);
                return;
            }
            const { sent_to: sentTo } = await response.json();
            showEntry(sentTo);
            startResendTimer();
        } catch (e) {
            console.error(e);
            setPanelError(null);
        } finally {
            setBusy(false);
        }
    };

    /** 確認コードでログインする（誤りなら同じチケットで再入力できる） */
    const submitCode = async () => {
        if (state.busy) return;
        const code = codeInput ? codeInput.value.trim() : '';
        if (code === '') {
            setPanelError('EMAIL_CODE_INVALID');
            return;
        }
        setBusy(true);
        setPanelError();
        try {
            const result = await login({ email_code: code });
            if (result.error === undefined) return;
            if (RESTART_ERRORS.includes(result.error)) {
                state.ticketConsumed = true;
                stopResendTimer();
                if (entryArea) entryArea.hidden = true;
                if (offerArea) offerArea.hidden = false;
            }
            setPanelError(result.error);
        } catch (e) {
            console.error(e);
            setPanelError(null);
        } finally {
            setBusy(false);
        }
    };

    if (sendButton) {
        sendButton.addEventListener('click', (e) => {
            e.preventDefault();
            if (!state.ticketConsumed && state.ticket) {
                // パスキーの署名待ちなら中断し、このチケットで確認コードを送る
                state.switchedToEmailCode = true;
                WebAuthnAbortService.cancelCeremony();
            }
            sendCode();
        });
    }
    if (resendButton) {
        resendButton.addEventListener('click', (e) => {
            e.preventDefault();
            sendCode();
        });
    }
    if (submitCodeButton) {
        submitCodeButton.addEventListener('click', (e) => {
            e.preventDefault();
            submitCode();
        });
    }
    if (codeInput) {
        codeInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                submitCode();
            }
        });
    }

    return { state, hide, showOffer, requestOptions, login };
};

/**
 * @param {Object} params
 * @param {HTMLFormElement} params.form ログインフォーム
 * @param {string} params.usernameKey ID入力欄の name（管理者 login / ユーザー email）
 * @param {string} params.optionsUrl
 * @param {string} params.loginUrl
 * @param {string} params.emailCodeUrl 確認コードの送信先（省略時は {loginUrl}/email-code）
 * @param {string} params.invalidCredentialsMessage INVALID_CREDENTIALS の表示文言
 * @param {?string} params.siteKey reCAPTCHA のサイトキー（無効時は null）
 * @param {string} params.actionName reCAPTCHA のアクション名
 */
export const submitPasskeyLogin = async ({
    form,
    usernameKey,
    optionsUrl,
    loginUrl,
    emailCodeUrl = null,
    invalidCredentialsMessage,
    siteKey = null,
    actionName = 'login',
}) => {
    const messages = { ...COMMON_ERROR_MESSAGES, INVALID_CREDENTIALS: invalidCredentialsMessage };
    const errorArea = form.querySelector('[data-login-error]');
    const submitButton = form.querySelector('[type="submit"]');

    const showError = (code) => {
        if (!errorArea) return;
        errorArea.textContent = messages[code] ?? DEFAULT_ERROR_MESSAGE;
        errorArea.hidden = false;
    };
    const clearError = () => {
        if (!errorArea) return;
        errorArea.textContent = '';
        errorArea.hidden = true;
    };

    const params = {
        usernameKey,
        optionsUrl,
        loginUrl,
        emailCodeUrl: emailCodeUrl ?? `${loginUrl}/email-code`,
        invalidCredentialsMessage,
        siteKey,
        actionName,
        clearError,
    };
    let session = sessions.get(form);
    if (!session) {
        session = createSession(form, params);
        sessions.set(form, session);
    }
    session.state.params = params;
    session.state.switchedToEmailCode = false;
    session.hide();

    if (submitButton) submitButton.disabled = true;
    clearError();

    try {
        // ① ID・パスワードの検証とチケット発行
        const optionsResult = await session.requestOptions();
        if (optionsResult.error !== undefined) {
            showError(optionsResult.error);
            return;
        }
        const { options } = optionsResult;

        // ② パスキー登録済みならパスキーで署名する
        let assertion = null;
        if (options) {
            // パスキーのない端末向けに、確認コードの案内を表示しておく
            session.showOffer();
            try {
                assertion = JSON.stringify(await startAuthentication({ optionsJSON: options }));
            } catch (e) {
                // 端末側のダイアログをキャンセルした場合や、手元にパスキー端末がない場合。
                // アサーションなしで続行し、サーバー側で PASSKEY_REQUIRED になる
                console.warn(e);
            }
            // 確認コードに切り替えた場合はチケットを残す（確認コードの送信に使う）
            if (session.state.switchedToEmailCode) return;
        }

        // ③ チケット（＋アサーション）でログイン
        const loginResult = await session.login({ assertion });
        if (loginResult.error !== undefined) {
            showError(loginResult.error);
            if (options && OFFER_EMAIL_CODE_ERRORS.includes(loginResult.error)) {
                // チケットは消費済み。ボタン押下時に画面の入力値で options を取り直す
                session.showOffer();
            } else {
                session.hide();
            }
            return;
        }
        return;
    } catch (e) {
        console.error(e);
        showError(null);
    } finally {
        if (submitButton) submitButton.disabled = false;
    }
};
